import { useEffect, useState } from 'react';
import type { TreeService } from '../service';
import type { UseExpandResult } from './types';
import type { DataSource, TreeKeys } from '../types';

export function useExpand(
  service: TreeService,
  dataSource: DataSource,
  expandRoot: boolean,
  searchValue: string,
  showSearch: boolean,
  defaultExpandedKeys?: TreeKeys
): UseExpandResult {
  // 受控展开的节点
  const [expandedKeys, setExpandedKeys] = useState<TreeKeys>(() => {
    if (defaultExpandedKeys == null) {
      return [];
    }
    return defaultExpandedKeys;
  });

  // 搜索时 受控的展开节点
  const [searchExpandedKeys, setSearchExpandedKeys] = useState<TreeKeys>([]);

  // 搜索时设置true，自动展开父节点
  const [autoExpandParent, setAutoExpandParent] = useState(false);

  const isSearching = showSearch && searchValue.length > 0;

  // 获取父组件的数据源，并更新受控展开的节点
  useEffect(() => {
    if (!expandRoot) {
      return;
    }
    const rootKeys = service.getRootNodes(dataSource);
    const uniquekeys = Array.from(new Set(expandedKeys.concat(rootKeys)));
    setExpandedKeys(uniquekeys);
  }, [dataSource, expandRoot]);

  // 搜索时展开匹配的节点及其父节点
  useEffect(() => {
    if (isSearching) {
      const keys = service.findExpandedByText(dataSource, searchValue);

      if (expandRoot && keys.length === 0) {
        const rootKeys = service.getRootNodes(dataSource);
        setSearchExpandedKeys(rootKeys);
      } else {
        setSearchExpandedKeys(keys);
      }
      setAutoExpandParent(true);
      return;
    }
    setAutoExpandParent(false);
  }, [dataSource, searchValue, isSearching, expandRoot]);

  /**
   * 节点展开回调
   */
  function onExpand(keys: TreeKeys): void {
    if (isSearching) {
      return setSearchExpandedKeys(keys);
    }
    setExpandedKeys(keys);
  }

  return {
    expandedKeys: isSearching ? searchExpandedKeys : expandedKeys,
    onExpand,
    autoExpandParent
  };
}
